/**
 * AdminSchoolWiringPage — at-a-glance "wiring health" for a school.
 *
 * Shows total counts (teachers/students/parents/classes/dual-roles), a list of
 * detected wiring issues (severity error/warning/info), and lets the admin
 * "look through" any user's eyes to see what they would see when logged in
 * (their class, attendance, fees, assigned exams, etc.).
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, AlertTriangle, AlertCircle, Info, Users, GraduationCap, UserCircle, BookOpen, Eye, RefreshCw, CheckCircle2, Wrench, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SEVERITY = {
  error:   { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  info:    { icon: Info, color: 'text-sky-600', bg: 'bg-sky-50 border-sky-200' },
};

const AdminSchoolWiringPage = () => {
  const { tenantId: paramTenant } = useParams();
  const [tenantId, setTenantId] = useState(paramTenant || '');
  const [activeUserId, setActiveUserId] = useState(null);
  const [fixDialog, setFixDialog] = useState(null); // {type, userId, name}
  const qc = useQueryClient();

  // List tenants so admin can pick which school to inspect.
  const { data: tenants = [] } = useQuery({
    queryKey: ['school-tenants'],
    queryFn: () => api.get('/school/tenants').then((r) => r.data),
  });

  React.useEffect(() => {
    if (!tenantId && tenants.length > 0) setTenantId(tenants[0].id);
  }, [tenants, tenantId]);

  const { data: health, isLoading, refetch } = useQuery({
    queryKey: ['school-wiring-health', tenantId],
    queryFn: () => api.get(`/school/admin/wiring/health/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });

  const { data: userDetail, isFetching: loadingUser } = useQuery({
    queryKey: ['school-wiring-user', tenantId, activeUserId],
    queryFn: () => api.get(`/school/admin/wiring/user/${tenantId}/${activeUserId}`).then((r) => r.data),
    enabled: !!tenantId && !!activeUserId,
  });

  const { data: options = { classes: [], students: [] } } = useQuery({
    queryKey: ['school-wiring-options', tenantId],
    queryFn: () => api.get(`/school/admin/wiring/options/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['school-wiring-health', tenantId] });
    qc.invalidateQueries({ queryKey: ['school-wiring-user', tenantId, activeUserId] });
  };

  const assignStudentClass = useMutation({
    mutationFn: (body) => api.post('/school/admin/wiring/assign-student-class', body),
    onSuccess: (r) => { toast.success(`Class assigned: ${r.data.class_name}`); invalidate(); setFixDialog(null); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const assignTeacherClasses = useMutation({
    mutationFn: (body) => api.post('/school/admin/wiring/assign-teacher-classes', body),
    onSuccess: (r) => { toast.success(`Teacher linked to ${r.data.classes_count} class(es)`); invalidate(); setFixDialog(null); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const linkParentChildren = useMutation({
    mutationFn: (body) => api.post('/school/admin/wiring/link-parent-children', body),
    onSuccess: (r) => { toast.success(`${r.data.children_count} children linked`); invalidate(); setFixDialog(null); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const bulkCreateFees = useMutation({
    mutationFn: (body) => api.post('/school/admin/wiring/bulk-create-fees', body),
    onSuccess: (r) => { toast.success(`Fee charges created: ${r.data.created} (skipped ${r.data.skipped})`); invalidate(); setFixDialog(null); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const backfillEnrollments = useMutation({
    mutationFn: () => api.post(`/school/admin/wiring/backfill-enrollments/${tenantId}`),
    onSuccess: (r) => {
      const { fixed, skipped, failed = [] } = r.data || {};
      toast.success(
        failed.length
          ? `Enrollments backfilled: ${fixed} fixed, ${skipped} skipped, ${failed.length} failed`
          : `Enrollments backfilled: ${fixed} fixed, ${skipped} already in place`
      );
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Backfill failed'),
  });

  // ---- Catchall students re-bucketing ---------------------------------------
  const { data: catchall = { students: [] } } = useQuery({
    queryKey: ['school-wiring-catchall', tenantId],
    queryFn: () => api.get(`/school/admin/wiring/catchall-students/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });
  const [catchallSelected, setCatchallSelected] = useState({});
  const [catchallTargetClassId, setCatchallTargetClassId] = useState('');
  const reassignStudents = useMutation({
    mutationFn: (body) => api.post('/school/admin/wiring/reassign-students', body),
    onSuccess: (r) => {
      const { moved, failed = [] } = r.data || {};
      toast.success(failed.length
        ? `${moved} students moved · ${failed.length} failed`
        : `${moved} students moved to their new class`);
      setCatchallSelected({});
      qc.invalidateQueries({ queryKey: ['school-wiring-catchall', tenantId] });
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Reassign failed'),
  });

  // ---- Bulk teacher → classes -----------------------------------------------
  const [bulkTeacherDialog, setBulkTeacherDialog] = useState(false);
  const [bulkTeacherSelected, setBulkTeacherSelected] = useState({});
  const [bulkTeacherClasses, setBulkTeacherClasses] = useState({});
  const bulkAssignTeachers = useMutation({
    mutationFn: (body) => api.post('/school/admin/wiring/bulk-assign-teachers-class', body),
    onSuccess: (r) => {
      toast.success(`${r.data.teachers_assigned} teachers linked to ${r.data.classes_count} class(es)`);
      setBulkTeacherDialog(false);
      setBulkTeacherSelected({});
      setBulkTeacherClasses({});
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Bulk assign failed'),
  });

  const counts = health?.counts || {};
  const issues = health?.issues || [];

  const byRole = {
    error: issues.filter((i) => i.severity === 'error'),
    warning: issues.filter((i) => i.severity === 'warning'),
    info: issues.filter((i) => i.severity === 'info'),
  };

  // Teachers without classes — derived from issues, used by the bulk-assign panel
  const unassignedTeachers = issues.filter(
    (i) => i.user_type === 'teacher' && i.message.includes('no classes assigned')
  );

  // Map issue → fix dialog opener based on message text + user_type
  const openFix = (iss) => {
    if (iss.user_type === 'student') {
      if (iss.message.startsWith('Student has no class') || iss.message.includes('does not match')) {
        setFixDialog({ type: 'student-class', user_id: iss.user_id, name: iss.name });
      } else if (iss.message.startsWith('No fee records')) {
        setFixDialog({ type: 'student-fee', user_id: iss.user_id, name: iss.name });
      } else {
        setActiveUserId(iss.user_id);
      }
    } else if (iss.user_type === 'teacher') {
      setFixDialog({ type: 'teacher-classes', user_id: iss.user_id, name: iss.name });
    } else if (iss.user_type === 'parent') {
      setFixDialog({ type: 'parent-children', user_id: iss.user_id, name: iss.name });
    }
  };

  // Bulk action: create same-amount fees for ALL students who have no fees yet.
  const bulkFixFees = () => {
    const studentIds = byRole.info
      .filter((i) => i.user_type === 'student' && i.message.startsWith('No fee records'))
      .map((i) => i.user_id);
    if (studentIds.length === 0) {
      toast.info('No students need fee setup.');
      return;
    }
    setFixDialog({ type: 'bulk-fees', student_ids: studentIds, name: `${studentIds.length} students` });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="school-wiring-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            School Wiring Health Check
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            At-a-glance view of what teachers, students and parents will actually see when they log in.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={tenantId}
            onChange={(e) => { setTenantId(e.target.value); setActiveUserId(null); }}
            className="border rounded px-2 py-1.5 text-sm bg-white"
            data-testid="wiring-tenant-select"
          >
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={() => refetch()} data-testid="wiring-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading wiring check…
        </div>
      ) : !health ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500">Select a school above to inspect.</CardContent></Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: counts + issues */}
          <div className="lg:col-span-2 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <CountTile icon={GraduationCap} label="Teachers" value={counts.teachers || 0} tone="indigo" />
              <CountTile icon={UserCircle} label="Students" value={counts.students || 0} tone="emerald" />
              <CountTile icon={Users} label="Parents" value={counts.parents || 0} tone="amber" />
              <CountTile icon={BookOpen} label="Classes" value={counts.classes || 0} tone="rose" />
              <CountTile icon={Eye} label="Dual roles" value={counts.dual_roles || 0} tone="sky" />
            </div>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    Wiring Issues
                    {issues.length === 0 && (
                      <Badge variant="default" className="bg-emerald-600">All clear ✓</Badge>
                    )}
                  </CardTitle>
                  {byRole.info.some((i) => i.user_type === 'student' && i.message.startsWith('No fee records')) && (
                    <Button size="sm" variant="outline" onClick={bulkFixFees} data-testid="wiring-bulk-fees">
                      <Wrench className="h-3.5 w-3.5 mr-1" /> Bulk-create fees for all students missing them
                    </Button>
                  )}
                  {byRole.error.some((i) => i.user_type === 'student' && i.message.includes('no active enrollment')) && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => backfillEnrollments.mutate()}
                      disabled={backfillEnrollments.isPending}
                      data-testid="wiring-backfill-enrollments"
                    >
                      {backfillEnrollments.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Wrench className="h-3.5 w-3.5 mr-1" />
                      )}
                      Backfill student enrollments (fixes blank portals)
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {issues.length === 0 ? (
                  <p className="text-sm text-slate-500 py-8 text-center">
                    Every user has the right wiring. Nothing to fix here.
                  </p>
                ) : (
                  <Tabs defaultValue={byRole.error.length ? 'error' : (byRole.warning.length ? 'warning' : 'info')}>
                    <TabsList>
                      <TabsTrigger value="error" data-testid="wiring-tab-error">
                        Errors ({byRole.error.length})
                      </TabsTrigger>
                      <TabsTrigger value="warning" data-testid="wiring-tab-warning">
                        Warnings ({byRole.warning.length})
                      </TabsTrigger>
                      <TabsTrigger value="info" data-testid="wiring-tab-info">
                        Info ({byRole.info.length})
                      </TabsTrigger>
                    </TabsList>
                    {['error', 'warning', 'info'].map((s) => (
                      <TabsContent key={s} value={s} className="pt-3 space-y-2 max-h-[55vh] overflow-y-auto">
                        {byRole[s].length === 0 ? (
                          <p className="text-xs text-slate-500 text-center py-6">Nothing here. 🎉</p>
                        ) : byRole[s].map((iss, i) => {
                          const meta = SEVERITY[iss.severity];
                          const Icon = meta.icon;
                          return (
                            <div
                              key={i}
                              className={`flex items-start gap-2 p-3 rounded border ${meta.bg}`}
                              data-testid={`wiring-issue-${i}`}
                            >
                              <Icon className={`h-4 w-4 mt-0.5 ${meta.color} flex-shrink-0`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-sm">{iss.name}</span>
                                  <Badge variant="outline" className="text-[10px]">{iss.user_type}</Badge>
                                </div>
                                <p className="text-xs text-slate-700 mt-0.5">{iss.message}</p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => openFix(iss)}
                                data-testid={`wiring-fix-${iss.user_id}`}
                              >
                                <Wand2 className="h-3.5 w-3.5 mr-1" /> Fix
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setActiveUserId(iss.user_id)}
                                data-testid={`wiring-inspect-${iss.user_id}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          );
                        })}
                      </TabsContent>
                    ))}
                  </Tabs>
                )}
              </CardContent>
            </Card>

            {/* P1 — Bulk reassignment of Catchall ("Unassigned Students") bucket */}
            {(catchall?.students?.length || 0) > 0 && (
              <Card data-testid="wiring-catchall-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        Catchall students · {catchall.students.length}
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-0.5">
                        These students are parked in the placeholder class &ldquo;{catchall.catchall_class_name}&rdquo;.
                        Pick a real class below and tick the students to move them.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={catchallTargetClassId}
                        onChange={(e) => setCatchallTargetClassId(e.target.value)}
                        className="border rounded px-2 py-1.5 text-sm bg-white"
                        data-testid="catchall-target-class"
                      >
                        <option value="">Pick target class…</option>
                        {options.classes
                          .filter((c) => c.id !== catchall.catchall_class_id)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} {c.grade ? `(${c.grade})` : ''}
                            </option>
                          ))}
                      </select>
                      <Button
                        size="sm"
                        onClick={() => {
                          const ids = Object.keys(catchallSelected).filter((k) => catchallSelected[k]);
                          if (!ids.length) return toast.error('Tick at least one student');
                          if (!catchallTargetClassId) return toast.error('Pick a target class');
                          reassignStudents.mutate({
                            tenant_id: tenantId,
                            student_ids: ids,
                            class_id: catchallTargetClassId,
                          });
                        }}
                        disabled={reassignStudents.isPending}
                        data-testid="catchall-reassign-btn"
                      >
                        {reassignStudents.isPending
                          ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          : <Wand2 className="h-3.5 w-3.5 mr-1" />}
                        Reassign {Object.values(catchallSelected).filter(Boolean).length || ''}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={
                          catchall.students.length > 0 &&
                          catchall.students.every((s) => catchallSelected[s.id])
                        }
                        onChange={(e) => {
                          const next = {};
                          if (e.target.checked) {
                            catchall.students.forEach((s) => { next[s.id] = true; });
                          }
                          setCatchallSelected(next);
                        }}
                        data-testid="catchall-select-all"
                      />
                      Select all
                    </label>
                    <span>
                      {Object.values(catchallSelected).filter(Boolean).length} of {catchall.students.length} selected
                    </span>
                  </div>
                  <div className="max-h-72 overflow-y-auto border rounded divide-y">
                    {catchall.students.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                        data-testid={`catchall-row-${s.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!catchallSelected[s.id]}
                          onChange={(e) =>
                            setCatchallSelected({ ...catchallSelected, [s.id]: e.target.checked })
                          }
                        />
                        <span className="flex-1">{s.name}</span>
                        <span className="text-xs text-slate-400">{s.email}</span>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* P1 — Bulk assign multiple teachers to one or more classes */}
            {unassignedTeachers.length > 0 && (
              <Card data-testid="wiring-bulk-teachers-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        Teachers without classes · {unassignedTeachers.length}
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Pick teachers + classes and link them all in one shot. Each teacher is added to every selected class.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setBulkTeacherDialog(true)}
                      data-testid="open-bulk-teachers"
                    >
                      <Wand2 className="h-3.5 w-3.5 mr-1" /> Bulk assign…
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="text-xs text-slate-500">
                  Latest unassigned: {unassignedTeachers.slice(0, 4).map((t) => t.name).join(', ')}
                  {unassignedTeachers.length > 4 && ` + ${unassignedTeachers.length - 4} more`}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: user inspector */}
          <div className="lg:col-span-1">
            <Card className="lg:sticky lg:top-4">
              <CardHeader>
                <CardTitle className="text-base">User Inspector</CardTitle>
                <p className="text-xs text-slate-500">
                  Click <em>Inspect</em> on any issue to see exactly what that user is seeing.
                </p>
              </CardHeader>
              <CardContent>
                {!activeUserId ? (
                  <p className="text-xs text-slate-500 text-center py-8">No user selected.</p>
                ) : loadingUser ? (
                  <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : userDetail ? (
                  <UserInspector detail={userDetail} />
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Fix dialog — modal per issue type */}
      <FixDialog
        fixDialog={fixDialog}
        onClose={() => setFixDialog(null)}
        options={options}
        tenantId={tenantId}
        assignStudentClass={assignStudentClass}
        assignTeacherClasses={assignTeacherClasses}
        linkParentChildren={linkParentChildren}
        bulkCreateFees={bulkCreateFees}
      />

      {/* Bulk-assign teachers → classes */}
      <Dialog open={bulkTeacherDialog} onOpenChange={(o) => !o && setBulkTeacherDialog(false)}>
        <DialogContent className="max-w-2xl" data-testid="bulk-teachers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-emerald-600" /> Bulk assign teachers to classes
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium mb-2">Teachers ({unassignedTeachers.length})</p>
              <div className="max-h-72 overflow-y-auto border rounded divide-y">
                {unassignedTeachers.map((t) => (
                  <label key={t.user_id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!bulkTeacherSelected[t.user_id]}
                      onChange={(e) => setBulkTeacherSelected({ ...bulkTeacherSelected, [t.user_id]: e.target.checked })}
                      data-testid={`bulk-teacher-row-${t.user_id}`}
                    />
                    <span>{t.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {Object.values(bulkTeacherSelected).filter(Boolean).length} selected
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Classes</p>
              <div className="max-h-72 overflow-y-auto border rounded divide-y">
                {options.classes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!bulkTeacherClasses[c.id]}
                      onChange={(e) => setBulkTeacherClasses({ ...bulkTeacherClasses, [c.id]: e.target.checked })}
                      data-testid={`bulk-class-row-${c.id}`}
                    />
                    <span>{c.name}</span>
                    {c.grade && <span className="text-xs text-slate-400">({c.grade})</span>}
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {Object.values(bulkTeacherClasses).filter(Boolean).length} selected
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkTeacherDialog(false)}>Cancel</Button>
            <Button
              onClick={() => {
                const teacher_ids = Object.keys(bulkTeacherSelected).filter((k) => bulkTeacherSelected[k]);
                const class_ids = Object.keys(bulkTeacherClasses).filter((k) => bulkTeacherClasses[k]);
                if (!teacher_ids.length) return toast.error('Pick at least one teacher');
                if (!class_ids.length) return toast.error('Pick at least one class');
                bulkAssignTeachers.mutate({ tenant_id: tenantId, teacher_ids, class_ids });
              }}
              disabled={bulkAssignTeachers.isPending}
              data-testid="bulk-teachers-submit"
            >
              {bulkAssignTeachers.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FixDialog = ({ fixDialog, onClose, options, tenantId,
                    assignStudentClass, assignTeacherClasses,
                    linkParentChildren, bulkCreateFees }) => {
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedClasses, setSelectedClasses] = useState({});
  const [selectedChildren, setSelectedChildren] = useState({});
  const [feeAmount, setFeeAmount] = useState(50);
  const [feeLabel, setFeeLabel] = useState('Term Fees');
  const [feeDate, setFeeDate] = useState('');

  React.useEffect(() => {
    // Reset every time a new fix is opened
    if (fixDialog) {
      setSelectedClassId('');
      setSelectedClasses({});
      setSelectedChildren({});
      setFeeAmount(50);
      setFeeLabel('Term Fees');
      setFeeDate('');
    }
  }, [fixDialog]);

  if (!fixDialog) return null;
  const { type, user_id, name, student_ids } = fixDialog;

  const titles = {
    'student-class':    `Assign a class to ${name}`,
    'teacher-classes':  `Assign classes to ${name}`,
    'parent-children':  `Link children to ${name}`,
    'student-fee':      `Create a fee charge for ${name}`,
    'bulk-fees':        `Create fees for ${name}`,
  };
  const submit = () => {
    if (type === 'student-class') {
      if (!selectedClassId) return toast.error('Pick a class');
      assignStudentClass.mutate({ tenant_id: tenantId, student_id: user_id, class_id: selectedClassId });
    } else if (type === 'teacher-classes') {
      const ids = Object.keys(selectedClasses).filter((k) => selectedClasses[k]);
      if (!ids.length) return toast.error('Pick at least one class');
      assignTeacherClasses.mutate({ tenant_id: tenantId, teacher_id: user_id, class_ids: ids });
    } else if (type === 'parent-children') {
      const ids = Object.keys(selectedChildren).filter((k) => selectedChildren[k]);
      if (!ids.length) return toast.error('Pick at least one child');
      linkParentChildren.mutate({ tenant_id: tenantId, parent_id: user_id, children_ids: ids });
    } else if (type === 'student-fee') {
      bulkCreateFees.mutate({
        tenant_id: tenantId,
        student_ids: [user_id],
        amount: Number(feeAmount),
        label: feeLabel,
        due_date: feeDate || null,
      });
    } else if (type === 'bulk-fees') {
      bulkCreateFees.mutate({
        tenant_id: tenantId,
        student_ids: student_ids || [],
        amount: Number(feeAmount),
        label: feeLabel,
        due_date: feeDate || null,
      });
    }
  };

  const pending = assignStudentClass.isPending || assignTeacherClasses.isPending
    || linkParentChildren.isPending || bulkCreateFees.isPending;

  return (
    <Dialog open={!!fixDialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="wiring-fix-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-emerald-600" />
            {titles[type] || 'Fix'}
          </DialogTitle>
        </DialogHeader>

        {type === 'student-class' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Pick the class this student belongs to. They&apos;ll start seeing attendance, exams, and fees once assigned.</p>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              data-testid="fix-class-select"
            >
              <option value="">Choose a class…</option>
              {options.classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.grade ? `(${c.grade})` : ''}</option>
              ))}
            </select>
          </div>
        )}

        {type === 'teacher-classes' && (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            <p className="text-sm text-slate-600">Tick all classes this teacher should see.</p>
            {options.classes.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm py-1">
                <input
                  type="checkbox"
                  checked={!!selectedClasses[c.id]}
                  onChange={(e) => setSelectedClasses({ ...selectedClasses, [c.id]: e.target.checked })}
                  data-testid={`fix-teacher-class-${c.id}`}
                />
                {c.name} {c.grade && <span className="text-xs text-slate-400">({c.grade})</span>}
              </label>
            ))}
            {options.classes.length === 0 && <p className="text-xs text-slate-500">No classes exist yet — create one in Class Management first.</p>}
          </div>
        )}

        {type === 'parent-children' && (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            <p className="text-sm text-slate-600">Tick the children this parent is responsible for.</p>
            {options.students.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm py-1">
                <input
                  type="checkbox"
                  checked={!!selectedChildren[s.id]}
                  onChange={(e) => setSelectedChildren({ ...selectedChildren, [s.id]: e.target.checked })}
                  data-testid={`fix-parent-child-${s.id}`}
                />
                {s.name}
              </label>
            ))}
            {options.students.length === 0 && <p className="text-xs text-slate-500">No students on this tenant.</p>}
          </div>
        )}

        {(type === 'student-fee' || type === 'bulk-fees') && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {type === 'bulk-fees'
                ? `This will create one fee charge per student in the list (${student_ids?.length || 0} students). Already-charged students are skipped.`
                : 'Create a single fee charge for this student.'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-slate-500 mb-1">Label</p>
                <Input value={feeLabel} onChange={(e) => setFeeLabel(e.target.value)} data-testid="fix-fee-label" />
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Amount (USD)</p>
                <Input type="number" min={1} value={feeAmount} onChange={(e) => setFeeAmount(e.target.value)} data-testid="fix-fee-amount" />
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Due date (optional)</p>
              <Input type="date" value={feeDate} onChange={(e) => setFeeDate(e.target.value)} data-testid="fix-fee-date" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={pending} data-testid="fix-submit">
            {pending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
            Apply fix
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CountTile = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-800',
    rose: 'bg-rose-50 text-rose-700',
    sky: 'bg-sky-50 text-sky-700',
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className={`inline-flex w-9 h-9 rounded-md items-center justify-center ${tones[tone] || tones.indigo}`}>
          <Icon className="h-5 w-5" />
        </div>
        <p className="text-2xl font-bold mt-2">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </CardContent>
    </Card>
  );
};

const UserInspector = ({ detail }) => {
  if (detail.user_type === 'teacher') {
    return (
      <div className="space-y-2 text-sm" data-testid="inspector-teacher">
        <Row label="Name" value={detail.name} />
        <Row label="Email" value={detail.email} />
        <Row label="Status" value={detail.status} />
        <hr />
        <Row label="Classes assigned" value={detail.classes?.length || 0} />
        {(detail.classes || []).map((c) => (
          <div key={c.id} className="text-xs text-slate-600 pl-3">
            • {c.name} <span className="text-slate-400">({c.students_count} students)</span>
          </div>
        ))}
        <Row label="Assignments authored" value={detail.assignments_authored || 0} />
        <Row label="Timetable slots" value={detail.timetable_slots || 0} />
      </div>
    );
  }
  if (detail.user_type === 'student') {
    return (
      <div className="space-y-2 text-sm" data-testid="inspector-student">
        <Row label="Name" value={detail.name} />
        <Row label="Email" value={detail.email} />
        <Row label="Status" value={detail.status} />
        <hr />
        <Row label="Class" value={detail.class?.name || '— none —'} warn={!detail.class?.exists} />
        <Row label="Attendance records" value={detail.attendance_records?.total || 0} />
        <div className="text-xs text-slate-500 pl-2">
          Manual: {detail.attendance_records?.manual_marking || 0} · Bus: {detail.attendance_records?.bus_scans || 0}
        </div>
        <hr />
        <Row label="Fee charges" value={detail.fees?.charges_count || 0} />
        <Row label="Total billed" value={`$${detail.fees?.total || 0}`} />
        <Row label="Total paid" value={`$${detail.fees?.paid || 0}`} />
        <Row label="Balance" value={`$${detail.fees?.balance || 0}`} warn={(detail.fees?.balance || 0) > 0} />
        <hr />
        <Row label="Assigned exams" value={detail.assigned_exams || 0} />
      </div>
    );
  }
  if (detail.user_type === 'parent') {
    return (
      <div className="space-y-2 text-sm" data-testid="inspector-parent">
        <Row label="Name" value={detail.name} />
        <Row label="Email" value={detail.email} />
        <hr />
        <Row label="Children linked" value={detail.children?.length || 0} />
        {(detail.children || []).map((c) => (
          <div key={c.id} className="text-xs text-slate-600 pl-3">
            • {c.name}
          </div>
        ))}
      </div>
    );
  }
  return <pre className="text-[10px] bg-slate-50 p-2 rounded">{JSON.stringify(detail, null, 2)}</pre>;
};

const Row = ({ label, value, warn }) => (
  <div className="flex justify-between items-baseline text-sm">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`font-medium ${warn ? 'text-amber-700' : ''}`}>{String(value)}</span>
  </div>
);

export default AdminSchoolWiringPage;
